import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { formatDateFilterValue } from 'src/helpers/dateFields';

export interface FacetItem {
  key: string;
  doc_count: number;
  label?: string;
}

interface DateRangeFilterProps {
  facets: FacetItem[];
  loading: boolean;
  fieldType: string;
  dateDisplayStyle: string;
  existingRange: { from: string; to: string } | null;
  onApplyRange: (from: string, to: string) => void;
  onBack: () => void;
}

const getPickerConfig = (fieldType: string) => {
  switch (fieldType) {
    case 'YO':
      return { views: ['year'] as const, format: 'yyyy', serialize: 'yyyy' };
    case 'YM':
      return { views: ['year', 'month'] as const, format: 'MMM yyyy', serialize: 'yyyy-MM' };
    default:
      return { views: ['year', 'month', 'day'] as const, format: 'MMM d, yyyy', serialize: 'yyyy-MM-dd' };
  }
};

/** Parse date string to local Date, avoiding UTC midnight timezone shifts. */
const parseDateString = (value: string): Date | null => {
  if (!value) return null;
  const parts = value.split('-').map(Number);
  if (parts.length === 1) return new Date(parts[0], 0, 1);
  if (parts.length === 2) return new Date(parts[0], parts[1] - 1, 1);
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  facets,
  loading,
  fieldType,
  dateDisplayStyle,
  existingRange,
  onApplyRange,
  onBack,
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const pickerConfig = getPickerConfig(fieldType);

  useEffect(() => {
    if (existingRange) {
      setFromDate(existingRange.from ? parseDateString(existingRange.from) : null);
      setToDate(existingRange.to ? parseDateString(existingRange.to) : null);
    }
  }, [existingRange]);

  /** Derive the min/max facet keys to show as a data range hint. */
  const dataRange = useMemo(() => {
    if (!facets || facets.length === 0) return null;
    const keys = facets.map((f) => f.key).sort();
    return {
      min: formatDateFilterValue(keys[0], fieldType, dateDisplayStyle),
      max: formatDateFilterValue(keys[keys.length - 1], fieldType, dateDisplayStyle),
    };
  }, [facets, fieldType, dateDisplayStyle]);

  const matchingBuckets = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const fromStr = format(fromDate, pickerConfig.serialize);
    const toStr = format(toDate, pickerConfig.serialize);
    return facets.filter((facet) => facet.key >= fromStr && facet.key <= toStr);
  }, [fromDate, toDate, facets, pickerConfig.serialize]);

  const totalCount = useMemo(() => {
    return matchingBuckets.reduce((sum, bucket) => sum + bucket.doc_count, 0);
  }, [matchingBuckets]);

  const handleApply = () => {
    if (!fromDate || !toDate || fromDate > toDate) return;
    const fromStr = format(fromDate, pickerConfig.serialize);
    const toStr = format(toDate, pickerConfig.serialize);
    onApplyRange(fromStr, toStr);
  };

  const textFieldSlotProps = {
    textField: {
      size: 'small' as const,
      fullWidth: true,
      sx: { '& .MuiInputBase-root': { fontSize: 13, height: 36 } },
    },
    openPickerButton: { size: 'small' as const, sx: { p: 0.25 } },
  };

  const labelSx = {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    color: 'text.secondary',
    mb: 0.25,
    display: 'block',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
      <Button
        size="small"
        startIcon={<ArrowBackIcon sx={{ fontSize: '14px !important' }} />}
        onClick={onBack}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          fontSize: 11,
          color: 'text.secondary',
          p: 0,
          minWidth: 0,
          '&:hover': { bgcolor: 'transparent', color: 'secondary.main' },
        }}
      >
        Back to list
      </Button>

      {dataRange && (
        <Typography
          variant="caption"
          sx={{ fontSize: 10, color: 'text.disabled', letterSpacing: 0.3, lineHeight: 1.2 }}
        >
          Available: {dataRange.min} – {dataRange.max}
        </Typography>
      )}

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              Start
            </Typography>
            <DatePicker
              views={pickerConfig.views}
              format={pickerConfig.format}
              value={fromDate}
              onChange={setFromDate}
              openTo="year"
              maxDate={toDate ?? undefined}
              disabled={loading}
              slotProps={textFieldSlotProps}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={labelSx}>
              End
            </Typography>
            <DatePicker
              views={pickerConfig.views}
              format={pickerConfig.format}
              value={toDate}
              onChange={setToDate}
              openTo="year"
              minDate={fromDate ?? undefined}
              disabled={loading}
              slotProps={textFieldSlotProps}
            />
          </Box>
        </Box>
      </LocalizationProvider>

      {fromDate && toDate && matchingBuckets.length > 0 && (
        <Box sx={{ mt: 0.25 }}>
          <Typography variant="caption" sx={{ ...labelSx, mb: 0.5 }}>
            Matching ({matchingBuckets.length})
          </Typography>
          <Box
            sx={{
              maxHeight: 120,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 3 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 1 },
            }}
          >
            {matchingBuckets.map((bucket) => (
              <Box
                key={bucket.key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.25,
                  px: 0.5,
                  fontSize: 12,
                  color: 'text.secondary',
                  borderRadius: 0.5,
                  '&:nth-of-type(even)': { bgcolor: 'rgba(255,255,255,0.02)' },
                }}
              >
                <span>{formatDateFilterValue(bucket.key, fieldType, dateDisplayStyle)}</span>
                <span style={{ fontSize: 11, opacity: 0.6 }}>{bucket.doc_count}</span>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              pt: 0.5,
              mt: 0.25,
              borderTop: '1px solid',
              borderColor: 'divider',
              fontSize: 12,
              fontWeight: 600,
              color: 'secondary.main',
            }}
          >
            <span>Total</span>
            <span>{totalCount}</span>
          </Box>
        </Box>
      )}

      <Button
        variant="outlined"
        size="small"
        disabled={!fromDate || !toDate}
        onClick={handleApply}
        sx={{
          mt: 0.5,
          textTransform: 'none',
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: 0.3,
          height: 32,
          borderColor: 'divider',
          '&:not(:disabled)': {
            borderColor: 'secondary.main',
            color: 'secondary.main',
          },
        }}
      >
        Apply range
      </Button>
    </Box>
  );
};

export default DateRangeFilter;
